<template>
  <section class="flex flex-col gap-4" data-testid="document-review-panel">
    <div
      v-if="isLoadingDetail && !document"
      class="h-[420px] rounded-lg bg-gray-100 animate-pulse"
      data-testid="document-review-panel-skeleton"
    />
    <div v-else class="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-5 w-full text-left">
      <DocumentViewer
        :version-id="viewerVersionId"
        :is-legacy="isLegacy"
        :is-restricted="isRestricted"
        :page-count="document?.currentVersion?.pageCount ?? null"
        :size-bytes="document?.currentVersion?.sizeBytes ?? null"
      />

      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-1">
          <p class="text-base font-bold text-textPrimary">{{ entry.document.name }}</p>
          <p v-if="document?.currentVersion" class="text-xs text-[#52526f]">
            versão {{ document.currentVersion.versionNumber }}
          </p>
          <p
            v-if="document?.currentVersion?.source === ProfessionalDocumentVersionSource.BACKFILL"
            class="text-xs font-semibold text-[#52526f]"
            data-testid="document-review-imported"
          >
            Importado da base da operação
          </p>
          <DocumentStatusBadge
            :status="status"
            :pending-reason="document?.pendingReason ?? entry.document.pendingReason"
            :rejection="document?.rejection ?? null"
            :expires-at="document?.expiresAt ?? null"
            :is-legacy="isLegacy"
          />
        </div>

        <dl class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px]">
          <template v-if="entry.document.submittedAt">
            <dt class="text-[#7f7f8e]">Enviado em</dt>
            <dd class="text-textPrimary">{{ formatDateTime(entry.document.submittedAt) }}</dd>
          </template>
          <template v-if="isInReview">
            <dt class="text-[#7f7f8e]">Na fila</dt>
            <dd><WaitingSince :since="entry.document.submittedAt" /></dd>
          </template>
          <template v-if="document?.expiresAt">
            <dt class="text-[#7f7f8e]">Validade</dt>
            <dd class="text-textPrimary">{{ formatIsoDate(document.expiresAt) }}</dd>
          </template>
          <template v-if="document?.authenticityCode">
            <dt class="text-[#7f7f8e]">Cód. autenticidade</dt>
            <dd class="text-textPrimary">{{ document.authenticityCode }}</dd>
          </template>
          <template v-if="showCrmCheck">
            <dt class="text-[#7f7f8e]">Verificação</dt>
            <dd :class="detail?.crmActive ? 'text-sucess' : 'text-negative'">
              CRM {{ detail?.crmActive ? 'Ativo' : 'não ativo' }} na base
            </dd>
          </template>
        </dl>

        <p
          v-if="document?.rejection?.guidance"
          class="rounded-lg bg-red-50 border border-red-200 p-2.5 text-[12px] text-textPrimary"
        >
          <b>Orientação enviada ao médico:</b> {{ document.rejection.guidance }}
        </p>
        <p
          v-if="document?.description"
          class="rounded-lg bg-[#f0f0f4] p-2.5 text-[12px] text-[#52526f]"
        >
          <b class="text-textPrimary">Como conferir:</b> {{ document.description }}
        </p>

        <div v-if="canReview && isInReview && !isRestricted" class="flex flex-col gap-2">
          <FilledButton
            text="Aprovar"
            width="100%"
            height="44px"
            :prepend-icon="mdiCheck"
            data-testid="document-review-approve"
            @click="emit('approve')"
          />
          <OutlineButton
            text="Reprovar"
            width="100%"
            height="44px"
            :prepend-icon="mdiClose"
            data-testid="document-review-reject"
            @click="emit('reject')"
          />
          <p v-if="showShortcuts" class="text-[11px] text-[#7f7f8e] text-center">
            Atalhos: <kbd class="kbd">A</kbd> aprovar · <kbd class="kbd">R</kbd> reprovar ·
            <kbd class="kbd">J</kbd>/<kbd class="kbd">K</kbd> próximo/anterior
          </p>
        </div>
        <div v-else-if="canReview && undoVersionId !== null" class="flex flex-col gap-1">
          <GhostButton
            text="Desfazer decisão"
            width="100%"
            height="40px"
            :prepend-icon="mdiUndoVariant"
            data-testid="document-review-undo"
            @click="undo"
          />
          <p class="text-[11px] text-[#7f7f8e] text-center">Volta o documento para Em Análise.</p>
        </div>
        <p
          v-else-if="status === ProfessionalDocumentStatus.PENDING"
          class="text-[12px] text-[#7f7f8e]"
        >
          Aguardando o médico enviar pelo app.
        </p>

        <DocumentHistory :events="document?.history ?? []" />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiCheck, mdiClose, mdiUndoVariant } from '@mdi/js'

import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import DocumentHistory from '@/components/ProfessionalDocuments/DocumentHistory.vue'
import DocumentStatusBadge from '@/components/ProfessionalDocuments/DocumentStatusBadge.vue'
import DocumentViewer from '@/components/ProfessionalDocuments/DocumentViewer.vue'
import { formatDateTime, formatIsoDate } from '@/components/ProfessionalDocuments/labels'
import {
  ProfessionalDocumentStatus,
  ProfessionalDocumentVerification,
  ProfessionalDocumentVersionSource
} from '@/types/professional-documents.types'
import type { ProfessionalDocumentsDetailResponse } from '@/types/professional-documents.types'

import type { ReviewQueueEntry } from '../model'
import WaitingSince from './WaitingSince.vue'

const props = withDefaults(
  defineProps<{
    entry: ReviewQueueEntry
    detail: ProfessionalDocumentsDetailResponse | null
    isLoadingDetail: boolean
    showShortcuts: boolean
    /** D112: só o Editor de Documentação (ou acima) decide e desfaz; o Leitor só vê. */
    canReview?: boolean
  }>(),
  { canReview: true }
)
const emit = defineEmits<{ approve: []; reject: []; undo: [versionId: number] }>()

const document = computed(
  () =>
    props.detail?.documents.find((item) => item.documentId === props.entry.document.documentId) ??
    null
)

/** O detalhe é mais novo que a fila (recarrega depois de cada decisão): ele manda quando existe. */
const status = computed(() => document.value?.status ?? props.entry.document.status)
const isLegacy = computed(() => document.value?.isLegacy ?? props.entry.document.isLegacy)
const isInReview = computed(() => status.value === ProfessionalDocumentStatus.IN_REVIEW)

/** Documento sem compartilhamento com a empresa de quem vê (D80, R14): sem abrir nem decidir. */
const isRestricted = computed(
  () => document.value !== null && document.value.currentVersion === null
)

/** Sem o detalhe ainda, a versão da fila já basta para abrir o arquivo. */
const viewerVersionId = computed(() =>
  document.value
    ? (document.value.currentVersion?.versionId ?? null)
    : props.entry.document.currentVersionId
)

/**
 * Legado sem arquivo (D11) nunca desfaz; versão importada (BACKFILL, A5) desfaz como as
 * demais — o backend diz qual versão é desfazível em `undoableVersionId`.
 */
const undoVersionId = computed(() =>
  isLegacy.value || isRestricted.value ? null : (document.value?.undoableVersionId ?? null)
)

function undo() {
  if (undoVersionId.value !== null) emit('undo', undoVersionId.value)
}

/** CRM na base só para tipos com conferência na fonte, e só se a base respondeu (`null` = sem consulta). */
const showCrmCheck = computed(
  () =>
    document.value !== null &&
    document.value.verification !== ProfessionalDocumentVerification.MANUAL &&
    props.detail?.crmActive != null
)
</script>

<style scoped>
.kbd {
  display: inline-block;
  padding: 0 4px;
  border: 1px solid #d8d8e2;
  border-radius: 3px;
  font-size: 10px;
  background: #fff;
}
</style>
